/*
 * Autorska prava pridržava Mali mol d.o.o. Pula
 * Copyright (c) 2016. by Mali mol d.o.o. Pula. All rights reserved.
 *
 * Korištenje ili javno objavljivanje ovog programskog koda ili bilo kojeg
 * njegovog dijela, nije dozvoljeno bez prethodnog pismenog odobrenja tvrtke Mali mol d.o.o.
 *
 * Mali mol d.o.o., Špiljavac 2, 52100 Pula, Croatia
 * <URL:http://www.malimol.hr>, <mailto:info@malimol.hr>
 */
import { ref, Ref } from "vue";
import { useApi } from "../modules/api";
import type { ICalculationRow } from "@/types/ICalculationRow";
import type { ISketchCalculation } from "@/types/ISketchCalculation";
import type { ICalculationRequest } from "@/types/ICalculationRequest";
import type { ICalculationSubassembly, ICalculationInfo } from "@/types/ICalculationInfo";
import { useResponseParser } from "@/modules/responseParser";
import { useAppStore } from "@/stores/app";

export const useCalculationService = () => {
  const pricelist: Ref<Array<ICalculationRow>> = ref([]);
  const sketchCalculations: Ref<Array<ISketchCalculation>> = ref([]);
  const calculations: Ref<Array<ICalculationInfo>> = ref([]);
  const totalRecords = ref(0);
  const work_price = ref(0);
  const margin = ref(0);
  const fluctuations = ref(0);
  const priceFactor = ref(0);
  const { get, post, del, loading, errorMessage, errorDetails } = useApi();
  const { processErrorResponse } = useResponseParser();
    const appStore = useAppStore();

  const calculateRow = (element: any, item: any, thickness: number, minor: number): ICalculationRow => {
    let waste = (1.0 - item.pricelist.waste / 100.0);
    let quantity = element.count * item.count;
    let mass = element.area * thickness * element.density * element.count;
    let mass_brutto = mass / waste;
    let total_time = item.price.value * quantity;
    let row: ICalculationRow = {
      article:
        item.pricelist._id +
        "." +
        item.price._id +
        "." +
        element.material +
        ".",
      position: element.ordinal + "." + minor + ".",
      element: item.pricelist.name,
      diameter: item.D ? item.D : element.D,
      LDR: item.pricelist.addition
        ? item.pricelist.D
        : element.length
        ? element.length.toString()
        : undefined,
      L1: element.L1,
      Lm: element.Lm,
      L2: element.L2,
      quantity: quantity,
      units: item.pricelist.units,
      waste: item.pricelist.waste,
      area_netto: item.pricelist.addition ? 0 : element.area * element.count,
      area_brutto: item.pricelist.addition
        ? 0
        : (element.area / waste) * element.count,
      material: item.pricelist.addition ? "" : element.materialName,
      thickness: item.pricelist.addition ? 0 : thickness * 1000,
      mass_netto: item.pricelist.addition ? 0 : mass,
      mass_brutto: item.pricelist.addition ? 0 : mass_brutto,
      work_time: total_time,
      work_price: (total_time / 60) * work_price.value,
      material_price: item.pricelist.addition
        ? 0
        : element.price * priceFactor.value * mass_brutto,
    };
    return row;
  };

  const calculateElements = (elements: any): Array<ICalculationRow> => {
    let list = [] as Array<ICalculationRow>;
    elements.forEach((element) => {
      let minor = 1;
      let thickness = element.thickness / 100000.0; // mm -> m
      element.calculations.forEach((item) => {
        list.push(calculateRow(element, item, thickness, minor));
        minor++;
      });
    });
    return list;
  };

  const refresh = (params?: string) => {
    return get("/api/calculate/", params ? { params: params } : {}).then(
      (res) => {
        if (!res.status) {
          processErrorResponse(res);
        } else {
          let items = res.data;
          items.forEach((item: ICalculationInfo) => {
            let content = JSON.parse(item.content);
            content.forEach((element: any) => {
              let subassemblyGuid = element.subassembly;
              let subassembly = item.subassemblies.find(
                (sa: ICalculationSubassembly) =>
                  sa.subassembly === subassemblyGuid
              );
              if (subassembly) {
                subassembly.total = element.totalPrice;
                subassembly.area_brutto = element.area_brutto;
                subassembly.mass_brutto = element.mass_brutto;
                subassembly.work_price = element.work_price;
                subassembly.material_price = element.material_price;
              }
            });
          });
          calculations.value = items;
          totalRecords.value = res.total;
        }
      }
    );
  };

  const calculate = (sketches: Array<string>) => {
    return post("/api/calculate/sketches", {
      subassemblies: sketches,
    }).then((res) => {
      if (!res.status) {
        processErrorResponse(res);
      } else {
        work_price.value = parseFloat(res.work_price);
        margin.value = res.margin;
        fluctuations.value = res.fluctuations;
        priceFactor.value = (1 + margin.value) * (1 + fluctuations.value);
        let items: Array<ISketchCalculation> = [];
        res.calculation.forEach(calculation => {
          let indexOfDash = calculation.id.indexOf("-");
          let creator = indexOfDash > 0 ? calculation.id.substring(0, indexOfDash) : calculation.id;
          let id = indexOfDash > 0 ? calculation.id.substring(indexOfDash+1) : calculation.id;
          let sketch: ISketchCalculation = {
            subassembly: calculation.subassembly,
            name: calculation.name,
            id: id,
            contract: calculation.contract,
            assembly: calculation.assembly,
            prices: calculateElements(calculation.prices),
            area_netto: 0,
            area_brutto: 0,
            mass_netto: 0,
            mass_brutto: 0,
            work_time: 0,
            work_price: 0,
            material_price: 0,
            totalPrice: 0,
            creator: creator,
          };
          sketch.prices.forEach((row) => {
            sketch.area_netto += row.area_netto!;
            sketch.area_brutto += row.area_brutto!;
            sketch.mass_netto += row.mass_netto!;
            sketch.mass_brutto += row.mass_brutto!;
            sketch.work_time += row.work_time;
            sketch.work_price += row.work_price;
            sketch.material_price += row.material_price;
            sketch.totalPrice += row.work_price + row.material_price;
          });
          items.push(sketch);
        });
        sketchCalculations.value = items;
        appStore.setCalculationSaved(false);
      }
    });
  };

  const insert = (request: ICalculationRequest) => {
    return post("/api/calculate", request).then((res) => {
      if (!res.status == true) {
        processErrorResponse(res);
      } else {
        appStore.setCalculationSaved(true);
        let name = btoa(res.data.fileName.trim());
        window.open("api/previewpdf?file=" + name, "_blank");
      }
    });
  };

  const remove = (id: number) => {
    return del("/api/calculate/" + id).then((res) => {
      if (res.status == true) {
        const itemIndex = calculations.value.findIndex(
          (item: ICalculationInfo) => item._id === id
        );
        if (itemIndex < 0) {
          return;
        }
        calculations.value.splice(itemIndex, 1);
      } else {
        processErrorResponse(res);
      }
    });
  };

  const getPreview = (id: number) => {
    return get("api/calculate/preview/" + id);
  };

  const reprint = (id: number, params: any) => {
    return post("api/calculate/print/" + id, params).then((res) => {
      if (res.status == true) {
        const itemIndex = calculations.value.findIndex(
          (item: ICalculationInfo) => item._id === id
        );
        if(itemIndex >= 0) {
          calculations.value[itemIndex].pdf_file = res.pdf_file;
        }
        let name = btoa(res.fileName.trim());
        window.open("api/previewpdf?file=" + name, "_blank");
      } else {
        processErrorResponse(res);
      }
    });
  };

  return {
    refresh,
    calculate,
    insert,
    remove,
    getPreview,
    reprint,
    calculations,
    totalRecords,
    pricelist,
    sketchCalculations,
    work_price,
    margin,
    fluctuations,
    priceFactor,
    loading,
    errorMessage,
    errorDetails,
  };
};
